import React from "react";
import PropTypes from "prop-types";
import Image from "next/image";
import styled from "styled-components";
import Button from "components/Button";

const OuterCardWrapper = styled.li`
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  list-style-type: none;
  padding-right: 1.25rem;
  width: 18.125rem;

  ${({ theme }) => theme.mq.xl`
    margin: 0;
    padding: 0 .75rem 0;
    text-align: center;

    :first-child {
      padding-left: 0;
    }

    :last-child {
      padding-right: 0;
    }
  `}

  ${({ theme }) => theme.mq.xxl`
    width: 19rem;
  `}
`;

const InnerCardWrapper = styled.a`
  display: flex;
  flex-direction: column;
`;

const ArticleInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem 0 0;

  ${({ theme }) => theme.mq.xl`
    padding: 0;
  `}
`;

const ArticleImage = styled.div`
  border: 0.1875rem solid transparent;
  border-radius: 1.875rem;
  height: 13.75rem;
  margin-bottom: 1.5rem;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primary};
  }

  ${({ theme }) => theme.mq.xl`
    height: 17.25rem;
  `}
`;

const ArticleTopic = styled.div`
  color: ${({ theme }) => theme.themeColors.brown};
  font-size: 0.75rem;
  min-width: 4.25rem;
  margin: 0 0 0.875rem;
  text-transform: uppercase;

  ${({ theme }) => theme.mq.xl`
    margin: 0 auto 0.875rem;
  `}
`;

const ArticleName = styled.h4`
  color: ${({ theme }) => theme.themeColors.darkGrey};
  display: -webkit-box;
  font-size: ${({ theme }) => theme.fontSize(4)};
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: 600;
  min-height: 4rem;
  letter-spacing: -0.025rem;
  margin-bottom: 0.5rem;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArticleDescription = styled.p`
  display: -webkit-box;
  font-size: ${({ theme }) => theme.fontSize(3)};
  line-height: 1.2;
  min-height: 6rem;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadMoreButton = styled(Button)`
  margin: 0;
`;

function ArticleCardBlock({ blockData }) {
  const {
    externalTitle,
    seoDescription,
    mainImage,
    publicUrl,
    mainTopic,
  } = blockData;
  return (
    <OuterCardWrapper>
      <InnerCardWrapper href={publicUrl} target="_blank">
        <ArticleImage>
          <Image
            alt={mainImage?.fields?.description}
            layout="fill"
            loading="eager"
            objectFit="cover"
            objectPosition="center"
            src={`https://${mainImage?.fields?.file?.url}`}
          />
        </ArticleImage>
        <ArticleInfo>
          <ArticleTopic>{mainTopic}</ArticleTopic>
          <ArticleName>{externalTitle}</ArticleName>
          <ArticleDescription>{seoDescription}</ArticleDescription>
          <ReadMoreButton type="button" outlineType="outline" colorType="brown">
            Read more on The Wildest
          </ReadMoreButton>
        </ArticleInfo>
      </InnerCardWrapper>
    </OuterCardWrapper>
  );
}

export default ArticleCardBlock;

ArticleCardBlock.propTypes = {
  blockData: PropTypes.shape({
    externalTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    publicUrl: PropTypes.string,
    mainTopic: PropTypes.string,
    mainImage: PropTypes.shape({
      fields: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }),
  }),
};

ArticleCardBlock.defaultProps = {
  blockData: {},
};
